import { useEffect, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import { emberApi, type PersonPageResponse } from '../api';
import { Link } from '../components/Link';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { PageLoading } from '../components/PageLoading';
import { PageFailure } from '../components/PageFailure';
import { PresenceAvatar } from '../components/PresenceAvatar';
import { timeAgo } from '../format';
import './PersonPage.css';

/**
 * S15 — the person page (v3.0 §4.16). Not an account, not a creator profile:
 * an anonymous person who persists. P01 is visible to anyone holding the
 * token; P02 lists ONLY what this viewer can already read — no totals, no
 * "N of M", no shape of anything they can't (§0.9 铁律). No follow, no
 * message, no stats; deliberately very quiet.
 */
export function PersonPage({ token }: { token: string }): ReactElement {
  const [data, setData] = useState<PersonPageResponse | null>(null);
  const [failed, setFailed] = useState<'load' | 'gone' | null>(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setFailed(null);
    emberApi.personPage(token)
      .then((next) => { if (alive) setData(next); })
      .catch((err: unknown) => {
        if (!alive) return;
        const status = (err as { status?: number })?.status;
        // Both exits are ui.failed facts: a dead/revoked presence token
        // (not_found) used to record nothing at all, so token rot was
        // invisible next to real load failures.
        if (status === 404) {
          trackFailed('person_page_load', 'not_found', {});
          setFailed('gone');
          return;
        }
        trackFailed('person_page_load', 'load_failed', {});
        setFailed('load');
      });
    return () => { alive = false; };
  }, [token]);

  if (failed === 'gone') return <PageFailure title="This page isn't here" logState="gone" />;
  if (failed === 'load') return <PageFailure title="Couldn't load this page" detail="Refresh to try again." logState="load_failed" />;
  if (!data) return <PageLoading />;

  const { presence, embers } = data;
  return (
    <main className="ember-container ember-reading person-page">
      <div className="person-toolbar">
        <PageBack className="person-back" fallbackLabel="Back" />
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>

      <header className="person-head" data-log-seen="person_head_seen">
        {presence.avatar_id ? <PresenceAvatar avatarKey={presence.avatar_id} size={72} /> : null}
        {presence.line ? <h1 className="person-line" dir="auto">{presence.line}</h1> : null}
        <p className="person-since">On Ember since {presence.since_month}</p>
        {presence.is_self ? (
          <p className="person-self-note">
            This is how readers see you.{' '}
            <Link
              to="/me/embers"
              className="ember-text-action"
              data-log="presence_space_open"
              data-log-discovery="person_page"
            >
              Edit your line
            </Link>
          </p>
        ) : null}
      </header>

      <section className="person-embers" aria-label={presence.is_self ? 'Your Embers' : 'Their Embers you can read'}>
        <h2 className="person-embers-title">{presence.is_self ? 'Your Embers' : 'Their Embers you can read'}</h2>
        {presence.is_self ? (
          // The owner is the only viewer who sees every listed Ember; each
          // reader's list is the subset they can already read (§4.16). Said
          // here, above the list, so the owner never mistakes it for what
          // others see (2026-09-02).
          <p className="person-self-scope">
            This full list is yours alone. Other readers see only the Embers they can already read.
          </p>
        ) : null}
        {embers.length === 0 ? (
          <p className="person-embers-empty">
            {presence.is_self ? 'Nothing here yet.' : 'No experiences of theirs you can read yet.'}
          </p>
        ) : (
          <ul className="person-ember-list">
            {embers.map((item, index) => {
              const href = item.entry.kind === 'direct'
                ? `/result/${encodeURIComponent(item.entry.conversation_id)}`
                // The owner's own Ember opens in the notebook, where they edit it.
                : `/me/embers/${encodeURIComponent(item.ember_id)}`;
              return (
                <li key={item.ember_id}>
                  <Link
                    to={href}
                    className={`person-ember-card${item.media?.header_image_url ? ' has-image' : ''}`}
                    data-log="person_ember_open"
                    data-log-seen="person_ember_open"
                    data-log-ember-id={item.ember_id}
                    data-log-position={index + 1}
                  >
                    {item.media?.header_image_url ? (
                      <img className="person-ember-thumb" src={item.media.header_image_url} alt="" loading="lazy" />
                    ) : null}
                    <span className="person-ember-text">
                      <span className="person-ember-title" dir="auto">{item.title || 'An Ember'}</span>
                      {item.entry.kind === 'direct' ? (
                        <span className="person-ember-context">Reached you directly</span>
                      ) : null}
                      {item.published_at ? (
                        <span className="person-ember-time">{timeAgo(item.published_at)}</span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
